import {
  format,
  isSameYear,
  isSameDay,
  fromUnixTime,
  formatDistanceToNow,
  differenceInDays,
} from 'date-fns';
import { getDateLocale, isRu } from './dateLocale';

const localeOpts = () => ({ locale: getDateLocale() });
const timeFmt = () => (isRu() ? 'HH:mm' : 'h:mm a');
const dateFmt = () => (isRu() ? 'd MMM yyyy' : 'MMM d, yyyy');
const dateTimeFmt = () => (isRu() ? 'd MMM yyyy, HH:mm' : 'LLL d y, h:mm a');

const to24hIfRu = fmt => {
  if (!isRu() || !fmt) return fmt;
  return fmt
    .replace(/h\.mmaaa/gi, 'HH:mm')
    .replace(/hh:mm a/gi, 'HH:mm')
    .replace(/h:mm a/gi, 'HH:mm');
};

export const messageStamp = (time, dateFormat) => {
  const unixTime = fromUnixTime(time);
  const fmt = to24hIfRu(dateFormat ?? timeFmt());
  return format(unixTime, fmt, localeOpts());
};

export const messageTimestamp = (time, dateFormat) => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  const fmt = to24hIfRu(dateFormat ?? dateFmt());
  const messageDate = format(messageTime, fmt, localeOpts());
  if (!isSameYear(messageTime, now)) {
    return format(messageTime, dateTimeFmt(), localeOpts());
  }
  return messageDate;
};

export const dynamicTime = time => {
  const unixTime = fromUnixTime(time);
  return formatDistanceToNow(unixTime, { addSuffix: true, ...localeOpts() });
};

export const dateFormat = (time, df) => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, df ?? dateFmt(), localeOpts());
};

export const dateTimeFormat = time => {
  const unixTime = fromUnixTime(time);
  const fmt = isRu() ? 'd MMM yyyy, HH:mm' : 'MMM dd, yyyy, h:mm a';
  return format(unixTime, fmt, localeOpts());
};

/**
 * Compact absolute timestamp for the conversation-list card.
 *   today          -> "в 12:34"         (ru) / "12:34 PM"          (en)
 *   this year      -> "12:32, 2 мая"     (ru) / "May 2, 12:34 PM"    (en)
 *   previous years -> "12:32, 2 мая 25"  (ru) / "May 2 25, 12:34 PM" (en)
 * @param {number} time - Unix timestamp in seconds.
 * @param {Date} [now] - Reference "now"; pass a reactive value to auto-refresh.
 * @returns {string}
 */
export const conversationListTimestamp = (time, now = new Date()) => {
  if (!time) return '';
  const date = fromUnixTime(time);
  let fmt;
  if (isSameDay(date, now)) {
    fmt = isRu() ? "'в' HH:mm" : 'h:mm a';
  } else if (isSameYear(date, now)) {
    fmt = isRu() ? 'HH:mm, d MMM' : 'MMM d, h:mm a';
  } else {
    fmt = isRu() ? 'HH:mm, d MMM yy' : 'MMM d yy, h:mm a';
  }
  return format(date, fmt, localeOpts());
};

const enMappings = (withAgo, suffix) => ({
  'less than a minute ago': 'now',
  'a minute ago': `1m${suffix}`,
  'an hour ago': `1h${suffix}`,
  'a day ago': `1d${suffix}`,
  'a month ago': `1mo${suffix}`,
  'a year ago': `1y${suffix}`,
});

const ruMappings = (withAgo, suffix) => ({
  'менее минуты назад': 'now',
  'минуту назад': `1м${suffix}`,
  'час назад': `1ч${suffix}`,
  'день назад': `1д${suffix}`,
  'месяц назад': `1мес${suffix}`,
  'год назад': `1г${suffix}`,
});

export const shortTimestamp = (time, withAgo = false) => {
  const suffix = withAgo ? (isRu() ? ' назад' : ' ago') : '';
  const en = enMappings(withAgo, suffix);
  const ru = ruMappings(withAgo, suffix);
  if (en[time]) return en[time];
  if (ru[time]) return ru[time];
  const enPat = time
    .replace(/about|over|almost|/g, '')
    .replace(' minute ago', `m${suffix}`)
    .replace(' minutes ago', `m${suffix}`)
    .replace(' hour ago', `h${suffix}`)
    .replace(' hours ago', `h${suffix}`)
    .replace(' day ago', `d${suffix}`)
    .replace(' days ago', `d${suffix}`)
    .replace(' month ago', `mo${suffix}`)
    .replace(' months ago', `mo${suffix}`)
    .replace(' year ago', `y${suffix}`)
    .replace(' years ago', `y${suffix}`);
  if (enPat !== time) return enPat;
  return time
    .replace(/(\d+)\s*минут[уы]?\s+назад/, (_, n) => `${n}м${suffix}`)
    .replace(/(\d+)\s*час[аов]?\s+назад/, (_, n) => `${n}ч${suffix}`)
    .replace(/(\d+)\s*дн[яей]+\s+назад/, (_, n) => `${n}д${suffix}`)
    .replace(/(\d+)\s*месяц[аев]?\s+назад/, (_, n) => `${n}мес${suffix}`)
    .replace(/(\d+)\s*лет\s+назад/, (_, n) => `${n}г${suffix}`)
    .replace(/(\d+)\s*год[аов]?\s+назад/, (_, n) => `${n}г${suffix}`)
    .replace(/минуту назад/, `1м${suffix}`)
    .replace(/час назад/, `1ч${suffix}`)
    .replace(/день назад/, `1д${suffix}`)
    .replace(/месяц назад/, `1мес${suffix}`)
    .replace(/год назад/, `1г${suffix}`);
};

/**
 * Formats a duration in seconds into mm:ss or hh:mm:ss.
 * @param {number|string} durationInSeconds - Duration in seconds.
 * @returns {string} Formatted duration string. Empty string for invalid input.
 */
export const formatDuration = durationInSeconds => {
  if (durationInSeconds === null || durationInSeconds === undefined) return '';

  const totalSeconds = Number(durationInSeconds);
  if (Number.isNaN(totalSeconds) || totalSeconds < 0) return '';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const mm = minutes.toString().padStart(2, '0');
  const ss = seconds.toString().padStart(2, '0');
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
